import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Ingredient, Step, CuisineType, FoodType, ConversionResult } from "@/types/recipe";
import { RecipeForm } from "@/components/recipe/RecipeForm";
import RecipePreview from "@/components/recipe/RecipePreview";

interface CreateRecipeFormProps {
  onSuccess?: () => void;
  initialData?: ConversionResult;
}

export const CreateRecipeForm = ({ onSuccess, initialData }: CreateRecipeFormProps) => {
  const navigate = useNavigate();
  const [title, setTitle] = useState(initialData?.title || "");
  const [description, setDescription] = useState(initialData?.description || "");
  const [ingredients, setIngredients] = useState<Ingredient[]>(
    initialData?.ingredients || [{ amount: 0, unit: "cup", name: "" }]
  );
  const [steps, setSteps] = useState<Step[]>(
    initialData?.steps || [{ instruction: "" }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<"public" | "subscription">("public");
  const [showPreview, setShowPreview] = useState(false);
  const [cuisineType, setCuisineType] = useState<CuisineType | ''>(
    (initialData?.cuisine_type as CuisineType) || ''
  );
  const [foodType, setFoodType] = useState<FoodType | ''>(
    (initialData?.food_type as FoodType) || ''
  );
  const [hasCalculatedNutrition, setHasCalculatedNutrition] = useState(!!initialData?.nutrition);
  const [nutritionalValues, setNutritionalValues] = useState<any>(initialData?.nutrition || null);
  const [servings, setServings] = useState(initialData?.servings || 1);

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('recipe-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('recipe-images')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };

  const validateRecipe = () => {
    if (!title.trim()) {
      toast.error("Please enter a recipe title");
      return false;
    }

    if (!cuisineType) {
      toast.error("Please select a cuisine type");
      return false;
    }

    if (!foodType) {
      toast.error("Please select a food type");
      return false;
    }

    if (ingredients.some(i => !i.name.trim() || i.amount <= 0)) {
      toast.error("Please fill in all ingredients with valid amounts");
      return false;
    }

    if (steps.some(s => !s.instruction.trim())) {
      toast.error("Please fill in all steps");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateRecipe()) return;

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please sign in to create a recipe");
        navigate("/login");
        return;
      }

      let imageUrl = null;
      if (imageFile) {
        try {
          imageUrl = await uploadImage(imageFile);
        } catch (error) {
          toast.error("Failed to upload image. Please try again.");
          return;
        }
      }

      const ingredientsJson = ingredients.map(ing => ({
        amount: Number(ing.amount),
        unit: ing.unit,
        name: ing.name.trim()
      }));

      const stepsJson = steps.map(step => ({
        instruction: step.instruction.trim(),
        temperature: step.temperature || null,
        time: step.time || null
      }));

      const { error: insertError } = await supabase
        .from('recipes')
        .insert({
          title: title.trim(),
          description: description.trim() || null,
          ingredients: ingredientsJson,
          steps: stepsJson,
          user_id: user.id,
          image_url: imageUrl,
          visibility,
          cuisine_type: cuisineType,
          food_type: foodType,
          servings,
          nutritional_values: nutritionalValues || {
            calories: 0,
            protein_g: 0,
            carbohydrates_g: 0,
            fat_g: 0,
            fiber_g: 0,
            sugar_g: 0,
            sodium_mg: 0
          }
        })
        .select()
        .single();

      if (insertError) {
        if (insertError.code === '23505') {
          toast.error("You already have a recipe with this title. Please choose a different title.");
        } else {
          console.error('Error creating recipe:', insertError);
          toast.error("Failed to publish recipe. Please try again.");
        }
        return;
      }

      toast.success("Recipe published successfully!");
      onSuccess?.();
      navigate("/dashboard");
    } catch (error) {
      console.error('Error creating recipe:', error);
      toast.error("Failed to publish recipe");
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateNutritionalValues = async () => {
    try {
      if (ingredients.some(i => !i.name || i.amount <= 0)) {
        toast.error("Please fill in all ingredients with valid amounts");
        return;
      }

      const { data, error } = await supabase.functions.invoke('calculate-nutrition', {
        body: { ingredients }
      });

      if (error) {
        console.error('Error calculating nutrition:', error);
        toast.error('Failed to calculate nutritional values');
        return;
      }

      setNutritionalValues(data);
      setHasCalculatedNutrition(true);
      toast.success('Nutritional values calculated!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to calculate nutritional values');
    }
  };

  if (showPreview) {
    return (
      <RecipePreview
        title={title}
        description={description}
        imagePreview={imagePreview}
        ingredients={ingredients}
        steps={steps}
        visibility={visibility}
        cuisineType={cuisineType}
        foodType={foodType}
        onBack={() => setShowPreview(false)}
      />
    );
  }

  return (
    <RecipeForm
      title={title}
      setTitle={setTitle}
      description={description}
      setDescription={setDescription}
      ingredients={ingredients}
      setIngredients={setIngredients}
      steps={steps}
      setSteps={setSteps}
      visibility={visibility}
      setVisibility={setVisibility}
      imageFile={imageFile}
      setImageFile={setImageFile}
      imagePreview={imagePreview}
      setImagePreview={setImagePreview}
      cuisineType={cuisineType}
      setCuisineType={setCuisineType}
      foodType={foodType}
      setFoodType={setFoodType}
      servings={servings}
      setServings={setServings}
      onPreview={() => setShowPreview(true)}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      onCalculateNutrition={calculateNutritionalValues}
      hasCalculatedNutrition={hasCalculatedNutrition}
      nutritionalValues={nutritionalValues}
      onNutritionalValuesEdit={setNutritionalValues}
    />
  );
};
